"use client";

import React from 'react';
import Link from 'next/link';
import { HouseIcon, TelegramIcon, AnimatedClapperboard } from '@/components/ui/Icons';
import { TELEGRAM_URL } from '@/config';
import { useRedirect } from '@/context/RedirectContext';

export default function NotFound() {
  const { redirect } = useRedirect();
  return (
    <div className="pt-20 md:pt-28 pb-16 min-h-[85vh] flex flex-col items-center justify-center relative px-4 overflow-hidden">
      {/* Cinematic Ambient Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] sm:w-[500px] h-[350px] sm:h-[500px] bg-red-600/10 dark:bg-red-600/15 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none z-0"></div>
      <div className="absolute top-1/3 left-1/4 w-[250px] h-[250px] bg-orange-500/5 dark:bg-orange-500/10 rounded-full blur-[60px] sm:blur-[100px] pointer-events-none z-0"></div>
      
      {/* Main Glassmorphic Card Container */}
      <main className="text-center z-10 max-w-xl w-full p-6 sm:p-10 rounded-[2.5rem] bg-[var(--card-bg)] backdrop-blur-xl border border-[var(--card-border)] shadow-2xl transition-all duration-300 hover:shadow-red-500/5 hover:border-red-500/20 group">
        
        {/* Interactive Movie Clapperboard - Reusable Icon from Icons.tsx */}
        <div className="relative w-36 h-36 mx-auto mb-6 flex items-center justify-center">
          <AnimatedClapperboard className="w-full h-full cursor-pointer select-none transition-transform duration-300 group-hover:scale-105" />
        </div>

        {/* 404 Headline */}
        <h1 className="text-7xl sm:text-8xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-red-500 to-pink-600 mb-2 select-none">
          404
        </h1>
        
        {/* Cinematic Title & Message */}
        <h2 className="text-2xl sm:text-3xl font-extrabold text-[var(--main-text)] mb-3">
          CUT! Scene Not Found
        </h2>
        <p className="text-[var(--secondary-text)] mb-8 max-w-md mx-auto text-sm sm:text-base leading-relaxed">
          The director couldn't find this scene in the script. It might have been deleted, or it was never filmed. Let's get you back on track!
        </p>




        {/* Movie Tickets Navigation */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
          {/* Ticket 1: Return to lobby */}
          <Link 
            href="/" 
            prefetch={false}
            className="relative flex items-center justify-center bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-3.5 px-8 rounded-xl shadow-lg shadow-red-500/10 transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] select-none overflow-hidden min-w-[200px] w-full sm:w-auto"
          >
            {/* Left Notch */}
            <span className="absolute -left-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-[var(--main-bg)] z-10 transition-colors duration-300"></span>
            {/* Right Notch */}
            <span className="absolute -right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-[var(--main-bg)] z-10 transition-colors duration-300"></span>
            {/* Vertical Ticket Dashed Divider */}
            <span className="absolute left-8 top-0 bottom-0 border-l border-dashed border-white/30 pointer-events-none"></span>
            
            {/* Icon on stub side */}
            <span className="absolute left-2.5 text-white/80">
              <HouseIcon className="w-4 h-4 ml-0.5" />
            </span>
            
            {/* Text on main side */}
            <span className="pl-4 tracking-wide text-sm sm:text-base">Return to Lobby</span>
          </Link>

          {/* Ticket 2: Join Telegram */}
          <button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              redirect("Telegram Channel", TELEGRAM_URL);
            }}
            className="relative flex items-center justify-center bg-[var(--button-bg)] hover:bg-[var(--button-hover-bg)] text-[var(--main-text)] font-bold py-3.5 px-8 rounded-xl shadow-md transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] select-none overflow-hidden min-w-[200px] w-full sm:w-auto border border-[var(--card-border)] cursor-pointer"
          >
            {/* Left Notch */}
            <span className="absolute -left-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-[var(--main-bg)] z-10 transition-colors duration-300"></span>
            {/* Right Notch */}
            <span className="absolute -right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-[var(--main-bg)] z-10 transition-colors duration-300"></span>
            {/* Vertical Ticket Dashed Divider */}
            <span className="absolute left-8 top-0 bottom-0 border-l border-dashed border-[var(--card-border)] pointer-events-none"></span>
            
            {/* Icon on stub side */}
            <span className="absolute left-2.5 text-[var(--secondary-text)]">
              <TelegramIcon className="w-4 h-4 ml-0.5" />
            </span>
            
            {/* Text on main side */}
            <span className="pl-4 tracking-wide text-sm sm:text-base">Join Telegram</span>
          </button>
        </div>

      </main>
    </div>
  );
}
